import { useCallback, useEffect, useId, useState } from 'react';

import { ApiError, api } from '../../api/client';
import {
  Alert,
  Button,
  Card,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  Section,
} from '../../components';
import { useI18n } from '../../i18n';
import { useSession } from '../../auth/session-context';

/**
 * Globale Einstellungen der Plattform (10_main_admin.md).
 *
 * Bewusst nur die Werte, die wirklich global sind. Alles Kundenspezifische
 * gehört in die Kundendetailseite, alles Kampagnenspezifische in die
 * Kampagne.
 */
interface PlatformSettings {
  defaultPricePerMinute: string;
  startCredit: string;
  /** Mindestbetrag fuer externe Guthabenaufladungen (09_billing_payments.md). */
  minTopUpAmount: string;
  retryMinIntervalMinutes: number;
  supportEmail: string | null;
}

export function PlatformSettingsPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();
  const preisId = useId();
  const guthabenId = useId();
  const mindestId = useId();
  const abstandId = useId();
  const supportId = useId();

  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError(null);
    try {
      const antwort = await api.get<{ settings: PlatformSettings }>('/platform/settings');
      setSettings(antwort.settings);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const speichern = (): void => {
    if (!settings) {
      return;
    }
    void (async () => {
      setError(null);
      setMessage(null);
      setBusy(true);
      try {
        await api.patch<{ settings: PlatformSettings }>('/platform/settings', {
          defaultPricePerMinute: settings.defaultPricePerMinute,
          startCredit: settings.startCredit,
          minTopUpAmount: settings.minTopUpAmount,
          retryMinIntervalMinutes: settings.retryMinIntervalMinutes,
          supportEmail: settings.supportEmail || null,
        });
        setMessage(t('platform.settings.saved'));
        await laden();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <PageLayout width="mittel">
      <PageHeader title={t('platform.settings.title')} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}
      {!settings && !error ? <LoadingState /> : null}

      {settings ? (
        <Card>
          <Section title={t('platform.settings.title')}>
            <FormField label={t('platform.settings.price')} htmlFor={preisId}>
              <Input
                id={preisId}
                value={settings.defaultPricePerMinute}
                onChange={(wert) => setSettings({ ...settings, defaultPricePerMinute: wert })}
              />
            </FormField>
            <FormField label={t('platform.settings.startCredit')} htmlFor={guthabenId}>
              <Input
                id={guthabenId}
                value={settings.startCredit}
                onChange={(wert) => setSettings({ ...settings, startCredit: wert })}
              />
            </FormField>
            <FormField
              label={t('platform.settings.minTopUp')}
              hint={t('platform.settings.minTopUpHint')}
              htmlFor={mindestId}
            >
              <Input
                id={mindestId}
                value={settings.minTopUpAmount}
                onChange={(wert) => setSettings({ ...settings, minTopUpAmount: wert })}
              />
            </FormField>
            <FormField label={t('platform.settings.retryInterval')} htmlFor={abstandId}>
              <Input
                id={abstandId}
                type="number"
                value={String(settings.retryMinIntervalMinutes)}
                onChange={(wert) =>
                  setSettings({ ...settings, retryMinIntervalMinutes: Number(wert) || 0 })
                }
              />
            </FormField>
            <FormField label={t('platform.settings.supportEmail')} htmlFor={supportId}>
              <Input
                id={supportId}
                type="email"
                value={settings.supportEmail ?? ''}
                onChange={(wert) => setSettings({ ...settings, supportEmail: wert })}
              />
            </FormField>

            <div className="row-actions">
              <Button variant="primary" disabled={busy} onClick={speichern}>
                {t('action.save')}
              </Button>
            </div>
          </Section>
        </Card>
      ) : null}

      <Card>
        <Section title="SMTP">
          <Alert tone="info">{t('platform.settings.smtpSoon')}</Alert>
        </Section>
      </Card>
    </PageLayout>
  );
}
